import { appConfig } from "@/lib/config";

type ErrorContext = Record<string, unknown>;

function serializeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ?? null,
    };
  }

  return {
    name: "UnknownError",
    message: typeof error === "string" ? error : "Unknown error",
    stack: null,
  };
}

function emitPayload(payload: unknown) {
  if (!appConfig.errorReportingEnabled || !appConfig.errorReportEndpoint) {
    if (appConfig.isDevelopment) {
      console.error("Captured application error", payload);
    }
    return;
  }

  const body = JSON.stringify(payload);

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(appConfig.errorReportEndpoint, blob);
    return;
  }

  void fetch(appConfig.errorReportEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function reportError(error: unknown, context: ErrorContext = {}) {
  const payload = {
    ...serializeError(error),
    context,
    route: typeof window !== "undefined" ? window.location.pathname : null,
    href: typeof window !== "undefined" ? window.location.href : null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    timestamp: new Date().toISOString(),
  };

  emitPayload(payload);
}

declare global {
  interface Window {
    __dhabaErrorHandlersInstalled?: boolean;
  }
}

export function setupGlobalErrorHandlers() {
  if (typeof window === "undefined" || window.__dhabaErrorHandlersInstalled) {
    return;
  }

  window.__dhabaErrorHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, {
      source: "window.error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, {
      source: "window.unhandledrejection",
    });
  });
}
